import type { CommentAttachment } from '@tourfold/libraries/tf-api-client-public-v2';

/**
 * Rewrites a comment's `body_markdown` so any resolved presigned URLs inside `![alt](...)` image
 * references are replaced with their canonical `attachment:<uuid>` token form.
 *
 * Background: the read-side API renders `body_markdown` with **resolved presigned URLs** in image
 * references (`![alt](https://s3.../<file-id>/...?X-Amz-…)`). That works for display, but the
 * edit-form seeds Tiptap with that same markdown — and when the user saves, the wire-side
 * `attachment_ids` extractor scans the body for `attachment:<uuid>` tokens, finds none in a
 * resolved-URL body, and submits an empty array. The server rejects with 422 (`disallowed-
 * markdown-construct:image` / `attachment-inline-not-linked`).
 *
 * Seeding the editor with the token form fixes that: Tiptap parses `![alt](attachment:<uuid>)`
 * into an `image` node whose `src` carries the token, the user edits freely, and on submit the
 * derived `attachment_ids` line up with the body content.
 *
 * The URL match is path-based (not full-URL), because the presigned URL's query signature
 * rotates between page-load and edit-submit. The file-id segment in the path stays stable, so
 * we identify which attachment a URL belongs to by comparing pathnames against
 * `attachment.downloadUrl` and `attachment.thumbnailUrl`.
 */
export function rewriteResolvedAttachmentUrls(
  bodyMarkdown: string,
  attachments: readonly CommentAttachment[],
): string {
  const inlineAttachments = attachments.filter((a) => a.isInline);
  if (inlineAttachments.length === 0) return bodyMarkdown;

  // Build a lookup keyed by pathname so we can match either downloadUrl or thumbnailUrl
  // regardless of which one ended up in the body. Both share the same file-id-rooted path, so
  // either match means "this is the same attachment".
  const pathToToken = new Map<string, string>();
  for (const att of inlineAttachments) {
    const token = `attachment:${att.filestoreFileId}`;
    if (att.downloadUrl) {
      const p = pathOf(att.downloadUrl);
      if (p) pathToToken.set(p, token);
    }
    if (att.thumbnailUrl) {
      const p = pathOf(att.thumbnailUrl);
      if (p) pathToToken.set(p, token);
    }
  }
  if (pathToToken.size === 0) return bodyMarkdown;

  // Match `![alt](url)` shapes. The alt text is preserved verbatim. The URL part can contain
  // anything except `)` (markdown image links don't support escaped parens, so this is the
  // right cut). Quoted URL titles (e.g. `![alt](url "title")`) are vanishingly rare on the
  // read path — the server emits the minimal form — so we don't try to round-trip them.
  return bodyMarkdown.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (whole, alt: string, url: string) => {
    if (url.startsWith('attachment:')) return whole;
    const path = pathOf(url);
    if (!path) return whole;
    const token = pathToToken.get(path);
    if (!token) return whole;
    return `![${alt}](${token})`;
  });
}

function pathOf(url: string): string | null {
  try {
    const base = globalThis.location?.origin ?? 'http://localhost';
    return new URL(url, base).pathname;
  } catch {
    return null;
  }
}
